/* eslint-disable react/prop-types */
import * as React from 'react';

import { useBoolean, useMemoizedFn, useRequest, useSafeState } from 'ahooks';
import {
  Button,
  Col,
  Divider,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd';

import {
  AntdUploadImgCrop,
  CopyOutlinedIcon,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  ExclamationCircleOutlinedIcon,
  InboxOutlinedIcon,
  InfoCircleOutlinedIcon,
  MenuOutlinedIcon,
  PlusOutlinedIcon,
  PrimitiveCollapse,
  PrimitiveForm,
  QuestionCircleFilledIcon,
  SortAscendingOutlinedIcon,
  UpOutlinedIcon,
} from 'components';
import {
  DND_CSS,
  dndArrayMove,
  DndSortableContext,
  useDndSortable,
  useProductAction,
  useProductValue,
} from 'context';
import { searchProducts } from 'services';
import type { ProductVariant, ProductVariantType, SelectOption } from 'types';
import {
  cn,
  generateDownloadFile,
  generateSelectOptions,
  inputRule,
  localeSortByKey,
  localizeProductVariantType,
  normalizeToString,
  PATHNAME,
  randomId,
} from 'utils';

const {
  QUERY_PARAM: {
    DEFAULT: {
      SORT_TYPE: DEFAULT_SORT_TYPE,
      PAGE_NUMBER: DEFAULT_PAGE_NUMBER,
      MAX_PAGE_SIZE: DEFAULT_MAX_PAGE_SIZE,
    },
  },
} = PATHNAME;

const MIN_SEARCH_CHARS = 3;

type ProductsParams = Parameters<typeof searchProducts>[number];

const productVariantTypes = ['File', 'Text'] satisfies ProductVariantType[];

type ModalFieldType = Prettify<
  Record<'product', SelectOption> &
    StrictOmit<ProductVariant, 'product' | 'identifier'>
>;

interface ProductVariantsInfoProductProps extends ProductVariant {
  openModal: () => void;
}

const ProductVariantsInfoProduct = React.memo<ProductVariantsInfoProductProps>(
  ({
    identifier,
    product: { name: productName },
    label,
    type,
    value,
    tooltip,
    openModal,
  }) => {
    const {
      attributes: sortableAttributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useDndSortable({
      id: identifier,
    });
    const { variants = [] } = useProductValue();
    const action = useProductAction();

    const defaultButtonProps: React.ComponentPropsWithoutRef<typeof Button> = {
      type: 'text',
      size: 'small',
    };
    const defaultColProps: React.ComponentPropsWithoutRef<typeof Col> = {
      span: 24,
      lg: 12,
      className: 'first:[&>span>span]:text-gray-400',
    };

    return (
      <PrimitiveCollapse
        ref={setNodeRef}
        collapsible='icon'
        style={{
          transform: DND_CSS.Transform.toString(transform),
          transition,
          ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
        }}
        expandIcon={({ isActive }) => (
          <UpOutlinedIcon
            className={cn(isActive ? 'rotate-0' : 'rotate-180')}
          />
        )}
        items={[
          {
            key: identifier,
            label: (
              <Space align='start' className='me-4'>
                <MenuOutlinedIcon {...sortableAttributes} {...listeners} />
                <Typography.Paragraph className='mb-0'>
                  <Typography.Text>{label}: </Typography.Text>
                  <Typography.Text type='secondary'>
                    {productName}
                  </Typography.Text>
                </Typography.Paragraph>
              </Space>
            ),
            extra: (
              <Space size={4}>
                <Tooltip title='حذف متغیر'>
                  <Popconfirm
                    title='تأیید حذف متغیر'
                    description='با حذف این متغیر، تمام اطلاعات مرتبط نیز پاک می‌شوند.'
                    okButtonProps={{
                      className: 'bg-[--ant-color-error]',
                    }}
                    icon={
                      <QuestionCircleFilledIcon className='text-[--ant-color-error]' />
                    }
                    onConfirm={() => {
                      const updatedVariants = variants.filter(
                        variant => variant.identifier !== identifier,
                      );

                      action({
                        type: 'UPDATE_PRODUCT_VARIANTS',
                        payload: {
                          variants: updatedVariants,
                          selectedVariant: undefined,
                        },
                      });
                    }}
                  >
                    <Button
                      icon={<DeleteOutlinedIcon />}
                      {...defaultButtonProps}
                    />
                  </Popconfirm>
                </Tooltip>
                <Tooltip title='کپی متغیر'>
                  <Popconfirm
                    title='تأیید کپی متغیر'
                    description='با کپی این متغیر، یک نسخه مشابه از آن ایجاد می‌شود.'
                    onConfirm={() => {
                      const duplicatedVariant = variants.find(
                        variant => variant.identifier === identifier,
                      );

                      if (duplicatedVariant) {
                        action({
                          type: 'UPDATE_PRODUCT_VARIANTS',
                          payload: {
                            variants: [
                              ...variants,
                              {
                                ...duplicatedVariant,
                                identifier: randomId(),
                              },
                            ],
                            selectedVariant: undefined,
                          },
                        });
                      }
                    }}
                  >
                    <Button
                      icon={<CopyOutlinedIcon />}
                      {...defaultButtonProps}
                    />
                  </Popconfirm>
                </Tooltip>
                <Tooltip title='ویرایش متغیر'>
                  <Button
                    icon={<EditOutlinedIcon />}
                    onClick={() => {
                      const selectedVariant = variants.find(
                        variant => variant.identifier === identifier,
                      );

                      if (selectedVariant) {
                        action({
                          type: 'UPDATE_PRODUCT_VARIANTS',
                          payload: {
                            variants,
                            selectedVariant,
                          },
                        });
                        openModal();
                      }
                    }}
                    {...defaultButtonProps}
                  />
                </Tooltip>
              </Space>
            ),
            children: (
              <Row gutter={[16, 20]}>
                <Col {...defaultColProps}>
                  <Typography.Text>
                    <span>نوع: </span>
                    <span>{localizeProductVariantType(type).label}</span>
                  </Typography.Text>
                </Col>
                <Col {...defaultColProps}>
                  <Typography.Text>
                    <span>توضیحات مختصر: </span>
                    <span>{tooltip || '-'}</span>
                  </Typography.Text>
                </Col>
                <Divider dashed className='my-1' />
                <Col {...defaultColProps}>
                  <Typography.Text>
                    <span>مقدار: </span>
                    <span>{value}</span>
                  </Typography.Text>
                </Col>
              </Row>
            ),
          },
        ]}
      />
    );
  },
);
ProductVariantsInfoProduct.displayName = 'ProductVariantsInfoProduct';

type ProductVariantsInfoModalProps = {
  open: boolean;
  closeModal: () => void;
};

const ProductVariantsInfoModal = React.memo<ProductVariantsInfoModalProps>(
  ({ open, closeModal }) => {
    const [form] = Form.useForm<ModalFieldType>();
    const currentValues = Form.useWatch(
      ({ type, value }) => ({
        currentType: type,
        currentValue: value,
      }),
      form,
    );
    const { selectedVariant, variants = [], ownerType } = useProductValue();
    const action = useProductAction();

    const {
      data: productsData,
      error: productsError,
      loading: isProductsLoading,
      run: searchProductsRun,
    } = useRequest(
      async ({ searchKey }: Pick<ProductsParams, 'searchKey'>) =>
        await searchProducts({
          searchKey,
          sortType: DEFAULT_SORT_TYPE,
          pageNumber: DEFAULT_PAGE_NUMBER,
          pageSize: DEFAULT_MAX_PAGE_SIZE,
        }),
      {
        debounceWait: 1000,
        manual: true,
      },
    );
    const [currentSearchKey, setCurrentSearchKey] = useSafeState<string>();

    const { total: totalProducts = 0, data: products = [] } =
      productsData ?? {};

    React.useEffect(() => {
      if (selectedVariant) {
        const {
          label,
          product: { id: productId, name: productName },
          type,
          value,
          tooltip,
        } = selectedVariant;

        form.setFieldsValue({
          label,
          type,
          value,
          tooltip,
          product: {
            key: productId,
            label: productName,
            value: productId,
          },
        });
        setCurrentSearchKey(productName);
        searchProductsRun({ searchKey: productName });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVariant]);

    const hasValidSearchInput = useMemoizedFn(
      (value: string = '') => value.trim().length >= MIN_SEARCH_CHARS,
    );

    const onCancel = useMemoizedFn(() => {
      action({
        type: 'UPDATE_PRODUCT_VARIANTS',
        payload: {
          variants,
          selectedVariant: undefined,
        },
      });
      form.resetFields();
      closeModal();
    });

    const onFinish = useMemoizedFn(
      ({ product, type, value, label, tooltip }: ModalFieldType) => {
        const defaultVariants = selectedVariant
          ? variants.filter(
              ({ identifier }) => identifier !== selectedVariant.identifier,
            )
          : variants;
        const newVariant: NonNullable<typeof selectedVariant> = {
          identifier: randomId(),
          product: products.find(({ id }) => id === product.value)!,
          type,
          value,
          label,
          tooltip,
        };

        action({
          type: 'UPDATE_PRODUCT_VARIANTS',
          payload: {
            variants: [newVariant, ...defaultVariants],
            selectedVariant: undefined,
          },
        });
        form.resetFields();
        closeModal();
      },
    );

    const { currentType, currentValue } =
      (currentValues as Partial<typeof currentValues>) ?? {};
    const productsSelectOptions = generateSelectOptions(
      products,
      'id',
      'name',
      'id',
    );
    const isFileVariant = currentType === 'File';

    if (!ownerType) return <React.Fragment />;

    return (
      <Modal
        centered
        destroyOnClose
        width={720}
        title={selectedVariant ? 'ویرایش متغیر موجود' : 'افزودن متغیر جدید'}
        okText={selectedVariant ? 'ذخیره تغییرات' : 'ساخت متغیر'}
        open={open}
        onOk={form.submit}
        onCancel={onCancel}
      >
        <Divider />
        <PrimitiveForm
          className='max-h-[calc(100dvh-15rem)] overflow-y-auto'
          form={form}
          onFinish={onFinish}
        >
          <Row>
            <Col span={24}>
              <Form.Item<ModalFieldType>
                label='برچسب'
                name='label'
                rules={[inputRule.required, inputRule.string]}
              >
                <Input placeholder='برچسب متغیر را وارد کنید' />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item<ModalFieldType>
                label='محصول'
                name='product'
                rules={[inputRule.required]}
              >
                <Select<SelectOption | undefined>
                  showSearch
                  labelInValue
                  filterOption={false}
                  popupMatchSelectWidth={false}
                  disabled={!!selectedVariant}
                  placeholder='محصول متغیر را انتخاب کنید'
                  searchValue={currentSearchKey}
                  options={
                    hasValidSearchInput(currentSearchKey)
                      ? productsSelectOptions
                      : undefined
                  }
                  notFoundContent={
                    isProductsLoading ? (
                      <div className='grid min-h-24 place-content-center gap-4'>
                        <Spin />
                        <Typography.Text className='text-[--ant-color-info]'>
                          در حال بارگذاری محصول‌ها...
                        </Typography.Text>
                      </div>
                    ) : productsError ? (
                      <div className='grid min-h-24 content-center justify-items-center gap-4'>
                        <ExclamationCircleOutlinedIcon className='text-2xl text-[--ant-color-error]' />
                        <Typography.Text type='danger'>
                          {productsError instanceof Error
                            ? productsError.message
                            : String(productsError)}
                        </Typography.Text>
                      </div>
                    ) : !hasValidSearchInput(currentSearchKey) ? (
                      <div className='grid min-h-24 content-center justify-items-center gap-4'>
                        <InfoCircleOutlinedIcon className='text-2xl' />
                        <Typography.Text type='secondary'>
                          لطفاً برای مشاهده محصول‌ها، حداقل {MIN_SEARCH_CHARS}{' '}
                          کاراکتر جستجو نمایید.
                        </Typography.Text>
                      </div>
                    ) : !totalProducts ? (
                      <div className='grid min-h-24 content-center justify-items-center gap-2.5'>
                        <InboxOutlinedIcon className='text-3xl' />
                        <Typography.Text type='secondary'>
                          <span>هیچ محصولی با عنوان</span>
                          <span> &rdquo;{currentSearchKey}&rdquo; </span>
                          <span>وجود ندارد.</span>
                        </Typography.Text>
                      </div>
                    ) : undefined
                  }
                  onChange={value => {
                    setCurrentSearchKey(normalizeToString(value?.label));
                  }}
                  onSearch={value => {
                    if (selectedVariant && !value) return;

                    setCurrentSearchKey(value);
                    if (hasValidSearchInput(value)) {
                      searchProductsRun({ searchKey: value });
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item<ModalFieldType>
                label='نوع'
                name='type'
                rules={[inputRule.required]}
              >
                <Select<ProductVariantType | undefined>
                  placeholder='نوع متغیر را انتخاب کنید'
                  options={productVariantTypes.map(type => {
                    const { label } = localizeProductVariantType(type);

                    return {
                      label,
                      value: type,
                    };
                  })}
                  onChange={typeValue => {
                    form.setFieldsValue({
                      type: typeValue,
                      value: undefined,
                    });
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item<ModalFieldType>
                label='مقدار'
                name='value'
                rules={[
                  inputRule.required,
                  ...(isFileVariant ? [] : [inputRule.string]),
                ]}
                valuePropName={isFileVariant ? 'file' : undefined}
              >
                {isFileVariant ? (
                  <AntdUploadImgCrop
                    ownerType={ownerType}
                    fileList={
                      currentValue
                        ? [
                            {
                              uid: currentValue,
                              name: 'مقدار متغیر',
                              status: 'done',
                              url: generateDownloadFile(
                                ownerType,
                                currentValue,
                              ),
                            },
                          ]
                        : undefined
                    }
                    onChange={({ file: { status, response } }) => {
                      form.setFieldValue('value', undefined);

                      if (status === 'done' && response) {
                        const { id } = response;
                        form.setFieldValue('value', id);
                      }
                    }}
                  />
                ) : (
                  <Input placeholder='مقدار متغیر را وارد کنید' />
                )}
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item<ModalFieldType>
                label='توضیحات مختصر (tooltip)'
                name='tooltip'
                rules={[inputRule.abstraction]}
              >
                <Input.TextArea
                  autoSize
                  placeholder='توضیحات مختصر متغیر را وارد کنید'
                />
              </Form.Item>
            </Col>
          </Row>
        </PrimitiveForm>
        <Divider />
      </Modal>
    );
  },
);
ProductVariantsInfoModal.displayName = 'ProductVariantsInfoModal';

const ProductVariantsInfo = () => {
  const [open, { set: setOpen }] = useBoolean();
  const { variants = [] } = useProductValue();
  const action = useProductAction();

  return (
    <Row>
      <Col span={24}>
        <Flex
          justify='space-between'
          gap='small'
          className='max-sm:flex-col sm:items-center'
        >
          <Typography.Title level={4} className='mb-0'>
            متغیر‌ها
          </Typography.Title>
          <Flex gap='small' className='flex-wrap *:flex-1'>
            <Button
              disabled={variants.length <= 1}
              icon={<SortAscendingOutlinedIcon />}
              onClick={() => {
                action({
                  type: 'UPDATE_PRODUCT_VARIANTS',
                  payload: {
                    variants: localeSortByKey(
                      variants,
                      'label',
                      'asc',
                      undefined,
                      { numeric: true },
                    ),
                    selectedVariant: undefined,
                  },
                });
              }}
            >
              مرتب‌سازی متغیرها
            </Button>
            <React.Fragment>
              <Button icon={<PlusOutlinedIcon />} onClick={() => setOpen(true)}>
                ساخت متغیر
              </Button>
              <ProductVariantsInfoModal
                open={open}
                closeModal={() => setOpen(false)}
              />
            </React.Fragment>
          </Flex>
        </Flex>

        <div className='my-4 overflow-auto'>
          {variants.length > 0 ? (
            <DndSortableContext
              dnd={{
                onDragEnd: ({ active, over }) => {
                  if (active.id !== over?.id) {
                    const activeIndex = variants.findIndex(
                      ({ identifier }) => identifier === active.id,
                    );
                    const overIndex = variants.findIndex(
                      ({ identifier }) => identifier === over?.id,
                    );
                    const sortedVariants = dndArrayMove(
                      variants,
                      activeIndex,
                      overIndex,
                    );

                    action({
                      type: 'UPDATE_PRODUCT_VARIANTS',
                      payload: {
                        variants: sortedVariants,
                        selectedVariant: undefined,
                      },
                    });
                  }
                },
              }}
              sortable={{
                items: variants.map(({ identifier = '' }) => identifier),
                children: variants.map(variant => (
                  <ProductVariantsInfoProduct
                    key={variant.identifier}
                    openModal={() => setOpen(true)}
                    {...variant}
                  />
                )),
              }}
            />
          ) : (
            <Empty
              className='my-20'
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className='text-zinc-300'>
                  هنوز متغیری ساخته نشده است.
                </span>
              }
            />
          )}
        </div>
      </Col>
    </Row>
  );
};

export {
  ProductVariantsInfo,
  ProductVariantsInfoModal,
  ProductVariantsInfoProduct,
};
